
import { useEffect, useRef } from 'react';

/**
 * @typedef {Object} Snowflake
 * @property {number} x
 * @property {number} y
 * @property {number} radius
 * @property {number} speed
 * @property {number} opacity
 */

export default function SnowEffect() {
  const canvasRef = useRef(/** @type {HTMLCanvasElement | null} */ (null));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const setCanvasSize = () => {
      try {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      } catch (error) {
        console.error('Error setting canvas size:', error);
      }
    };

    setCanvasSize();
    
    const handleResize = () => {
      setCanvasSize();
    };
    
    window.addEventListener('resize', handleResize);

    /** @type {Snowflake[]} */
    const snowflakes = [];
    const snowflakeCount = 100;
    
    // Check if mobile
    const isMobile = window.innerWidth < 768;
    const speedMultiplier = isMobile ? 0.5 : 1.5;

    try {
      for (let i = 0; i < snowflakeCount; i++) {
        snowflakes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          radius: Math.random() * 3 + 1,
          speed: (Math.random() * 1 + 0.5) * speedMultiplier,
          opacity: Math.random() * 0.6 + 0.4,
        });
      }
    } catch (error) {
      console.error('Error initializing snowflakes:', error);
      window.removeEventListener('resize', handleResize);
      return;
    }

    let animationFrameId = null;

    const animate = () => {
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        snowflakes.forEach((flake) => {
          ctx.beginPath();
          ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${flake.opacity})`;
          ctx.fill();

          flake.y += flake.speed;

          if (flake.y > canvas.height) {
            flake.y = -10;
            flake.x = Math.random() * canvas.width;
          }
        });

        animationFrameId = requestAnimationFrame(animate);
      } catch (error) {
        console.error('Error in animation frame:', error);
      }
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
